import * as React from 'react';

import { useQuery } from '@apollo/client';
import { IdTypes, createFeatureScope } from '@coursera-web/product-features';

import user from 'js/lib/user';

import HasUserEnforcedSSOLoginQuery from 'bundles/account-settings/queries/HasUserEnforcedSSOLogin.graphql';
import type {
  HasUserEnforcedSsoLoginQuery as HasUserEnforcedSsoLoginQueryType,
  HasUserEnforcedSsoLoginQueryVariables,
} from 'bundles/account-settings/queries/__generated__/HasUserEnforcedSSOLogin';
import { GdprSettingsQuery } from 'bundles/saml-sso/components/queries/GdprSettingsQuery';
import type {
  GdprSettingsQuery as GdprSettingsQueryType,
  GdprSettingsQueryVariables,
  GdprSettingsQuery_GdprSettingsV1Resource_get as GdprSettingsV1,
} from 'bundles/saml-sso/components/queries/__generated__/GdprSettingsQuery';
import useForcedSsoEpicEnabled from 'bundles/saml-sso/shared/hooks/useForcedSsoEpicEnabled';

const { useProductFeatures } = createFeatureScope({
  enterprise: {
    enableUnifiedSso: false,
  },
});

export type EnterpriseUserFeaturesContextType = {
  isUserSsoLoginForced: boolean;
  isUnifiedSso: boolean;
  gdprSettings: GdprSettingsV1 | null;
};

type EnterpriseUserFeaturesProviderProps = React.PropsWithChildren<{
  thirdPartyOrganizationIds?: string[];
}>;

type EnterpriseUserFeaturesConsumerProps = {
  children: (context: EnterpriseUserFeaturesContextType) => React.ReactElement;
};

export const EnterpriseUserFeaturesContext = React.createContext<EnterpriseUserFeaturesContextType | undefined>(
  undefined
);

export const EnterpriseUserFeaturesProvider = ({
  children,
  thirdPartyOrganizationIds = [],
}: EnterpriseUserFeaturesProviderProps) => {
  const { id: userId, email_address: emailAddress } = user.get();
  const { isEmailAllocatedForForceSsoEpic, loading: userEpicForceSsoLoading } = useForcedSsoEpicEnabled(emailAddress);
  const thirdPartyOrganizationId = thirdPartyOrganizationIds?.[0] || '';
  const { data: gdprSettingsData, loading: gdprSettingsLoading } = useQuery<
    GdprSettingsQueryType,
    GdprSettingsQueryVariables
  >(GdprSettingsQuery, {
    variables: { thirdPartyOrganizationId },
    skip: !thirdPartyOrganizationId,
    ssr: false,
  });

  const { features, loading: productFeaturesLoading } = useProductFeatures({
    idType: IdTypes.Organization,
    // Get the first organization ID to check for product features
    id: thirdPartyOrganizationId,
    skip: !thirdPartyOrganizationId,
    ssr: true,
  });

  const { data: userForcedSsoData, loading: userForceSsoLoading } = useQuery<
    HasUserEnforcedSsoLoginQueryType,
    HasUserEnforcedSsoLoginQueryVariables
  >(HasUserEnforcedSSOLoginQuery, {
    skip: !isEmailAllocatedForForceSsoEpic,
    variables: { userId: userId.toString() },
    context: { clientName: 'gatewayGql' },
  });

  // Compute isUserSsoLoginForced only when both Epic and user SSO loading are complete
  // Otherwise return false as default
  const isUserSsoLoginForced =
    !userEpicForceSsoLoading && !userForceSsoLoading
      ? !!userForcedSsoData?.EntSso?.queryIfUserNeedSsoLoginByUserIdV2?.isSsoLoginForced &&
        isEmailAllocatedForForceSsoEpic
      : false;

  // Once isUnifiedSso is rolled out to all organizations, we can remove the useProductFeatures
  // and directly add a prop that just checks if there are any programs || orgIds associated
  // with this user. These props are already available in the above sections of compose.
  //
  // Compute isUnifiedSso only when product features loading is complete
  // and user is enterprise user
  // otherwise return false as default
  const isUnifiedSso =
    !productFeaturesLoading && thirdPartyOrganizationId ? features.get('enterprise', 'enableUnifiedSso') : false;

  // Compute gdprSettings only when loading is complete, otherwise return null as default
  const gdprSettings = !gdprSettingsLoading ? (gdprSettingsData?.GdprSettingsV1Resource?.get ?? null) : null;

  return (
    <EnterpriseUserFeaturesContext.Provider value={{ isUserSsoLoginForced, isUnifiedSso, gdprSettings }}>
      {children}
    </EnterpriseUserFeaturesContext.Provider>
  );
};

export const useEnterpriseUserFeatures = () => {
  const context = React.useContext(EnterpriseUserFeaturesContext);

  if (!context) {
    throw new Error('useEnterpriseUserFeatures must be used within a EnterpriseUserFeaturesProvider');
  }

  return context;
};

export const EnterpriseUserFeaturesConsumer = ({ children }: EnterpriseUserFeaturesConsumerProps) => {
  const context = useEnterpriseUserFeatures();
  return children(context);
};
